import {
    Directive,
    ElementRef,
    HostListener,
    Input,
    AfterViewInit
} from '@angular/core';

/**
 * use to focus on a specific element when do tab or shift tab on the current element
 * we can also force to go to next focusable element automaticly
 */
@Directive({
    selector: '[appFocusBoundary]'
})
export class FocusBoundaryDirective implements AfterViewInit {
    @Input() nextId?: string;// ID de l’élément à focus sur tab
    @Input() prevId?: string; // ID de l’élément à focus avant sur shift tab
    @Input() forceToNextByCode = false; // force sur l'element suivant automatiquement par le code

    private focusables: HTMLElement[] = [];

    constructor(private el: ElementRef<HTMLElement>) {
    }

    ngAfterViewInit(): void {
        this.refreshFocusableElements();
    }

    // got to next element focusable automaticly when focus is on the current element
    @HostListener('focus', ['$event'])
    onFocus(event: FocusEvent): void {
        if (!this.forceToNextByCode || event.target !== this.el.nativeElement) {
            return;
        }

        this.refreshFocusableElements();
        const firstFocusable = this.focusables[0];
        if (firstFocusable) {
            Promise.resolve().then(() => firstFocusable.focus());
        }
    }

    // when do tab or shift tab go to a target with an id
    @HostListener('keydown', ['$event'])
    onKeyDown(event: KeyboardEvent): void {
        if (event.key !== 'Tab') {
            return;
        }

        if (this.focusables.length === 0) {
            return;
        }

        this.refreshFocusableElements();
        const firstElement = this.focusables[0];

        if (event.shiftKey && this.prevId) {
            if ((document.activeElement === firstElement && this.forceToNextByCode)
                || (document.activeElement === document.getElementById(this.nextId))) {
                event.preventDefault();
                const prevEl = document.getElementById(this.prevId);
                if (prevEl) {
                    prevEl.focus();
                }
            }
        }

        if (!event.shiftKey && this.nextId && document.activeElement !== this.el.nativeElement) {
            event.preventDefault();
            const nextEl = document.getElementById(this.nextId);
            if (nextEl) {
                nextEl.focus();
            }
        }
    }

    private refreshFocusableElements(): void {
        const candidates = this.el.nativeElement.querySelectorAll<HTMLElement>(
            `
        a[href],
        button:not([disabled]),
        input:not([disabled]),
        select:not([disabled]),
        textarea:not([disabled]),
        [tabindex]:not([tabindex="-1"])
      `
        );

        this.focusables = Array.from(candidates).filter(el =>
            !el.hasAttribute('aria-hidden') &&
            (el.offsetWidth > 0 || el.offsetHeight > 0 || el.getClientRects().length > 0)
        );
    }
}
